/**
 * Pure KMZ / KML parser. No DOM rendering, no map APIs — runs anywhere DOMParser exists
 * (browsers and jsdom test envs alike). Outputs a typed boundary-candidate list and a
 * classified layer list. The wizard UI consumes these to surface the polygon as the site
 * boundary and to register utilities / constraints / planned zones as `siteLayers`.
 *
 * Coordinate convention: every output point is `{ lat, lng }` (Google/Leaflet shape).
 * GeoJSON's `[lng, lat]` is converted at this boundary so downstream code never sees it.
 *
 * The format-agnostic feature walk (geometry extraction, boundary/layer split, scoring,
 * area math) lives in `geoImport/geojsonToLayers.ts`, shared with the GeoJSON and
 * shapefile importers. This module owns only the KML-specific parts: KMZ unzip,
 * XML parsing, the Placemark↔feature lockstep walk, and folder-path recovery.
 */

import JSZip from 'jszip';
import { kml as kmlToGeoJSON } from '@tmcw/togeojson';
import type { FeatureCollection, Geometry } from 'geojson';
import {
  featuresToParsedLayers,
  type GeoFeatureItem,
  type ParsedGeoImport,
} from './geoImport/geojsonToLayers';

// Re-exported so the 15+ existing consumers keep a single import site.
export type {
  LatLng,
  LayerCategory,
  LayerGeometryType,
  ParsedLayer,
  PolygonCandidate,
} from './geoImport/geojsonToLayers';

/** Historical name for the KMZ parse result; identical to ParsedGeoImport. */
export type ParsedKmz = ParsedGeoImport;

/* ── Public API ───────────────────────────────────────────────────────── */

export async function parseKmzOrKml(file: File): Promise<ParsedKmz> {
  const fileName = file.name;
  const lower = fileName.toLowerCase();

  if (lower.endsWith('.kmz')) {
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const entry = Object.values(zip.files).find(f => !f.dir && /\.kml$/i.test(f.name));
    if (!entry) {
      return { fileName, boundaries: [], layers: [], warnings: ['No KML document found inside the KMZ archive'] };
    }
    const text = await entry.async('text');
    return parseKmlText(text, fileName);
  }

  // .kml — read as text directly.
  const text = await file.text();
  return parseKmlText(text, fileName);
}

export function parseKmlText(text: string, fileName: string = 'unknown.kml'): ParsedKmz {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    return { fileName, boundaries: [], layers: [], warnings: ['Failed to parse KML XML'] };
  }

  // Pre-walk the DOM so each Placemark knows its ancestor folder chain.
  // togeojson does not preserve folder structure, so we do this in parallel.
  const folderPathByPlacemark = collectFolderPaths(doc);
  const placemarks = Array.from(doc.getElementsByTagName('Placemark'));

  const fc: FeatureCollection<Geometry | null> = kmlToGeoJSON(doc as unknown as Document);

  // togeojson emits one feature per Placemark in document order. We walk both lists in lockstep.
  const items: GeoFeatureItem[] = [];
  for (let i = 0; i < fc.features.length; i++) {
    const feature = fc.features[i];
    const placemark = placemarks[i];
    const folderPath = placemark ? folderPathByPlacemark.get(placemark) ?? [] : [];
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const name = typeof props.name === 'string' && props.name.trim() !== '' ? props.name : `Unnamed ${i + 1}`;
    const description = readDescription(props.description);

    if (!feature.geometry) continue;
    items.push({ geometry: feature.geometry, name, folderPath, description });
  }

  return featuresToParsedLayers(items, fileName);
}

/**
 * togeojson surfaces plain-text descriptions as a string, but CDATA-wrapped
 * descriptions arrive as `{ "@type": "html", value: "..." }`. We accept both.
 */
function readDescription(raw: unknown): string | undefined {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && 'value' in raw) {
    const v = (raw as { value: unknown }).value;
    return typeof v === 'string' ? v : undefined;
  }
  return undefined;
}

/* ── Folder path collection (DOM walk, parallel to togeojson) ─────────── */

function collectFolderPaths(doc: Document): Map<Element, string[]> {
  const map = new Map<Element, string[]>();
  const root = doc.documentElement;
  if (!root) return map;

  const visit = (node: Element, path: string[]): void => {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      const tag = stripNs(child.tagName);
      if (tag === 'Folder') {
        const folderName = directChildText(child, 'name');
        const nextPath = folderName ? [...path, folderName] : path;
        visit(child, nextPath);
      } else if (tag === 'Placemark') {
        map.set(child, path);
      } else {
        // Recurse into Document, kml, MultiGeometry, etc. — they're not path-bearing.
        visit(child, path);
      }
    }
  };

  visit(root, []);
  return map;
}

function stripNs(tagName: string): string {
  const idx = tagName.indexOf(':');
  return idx >= 0 ? tagName.slice(idx + 1) : tagName;
}

function directChildText(parent: Element, tagLocalName: string): string | null {
  for (let i = 0; i < parent.children.length; i++) {
    const c = parent.children[i];
    if (stripNs(c.tagName) === tagLocalName) return (c.textContent ?? '').trim() || null;
  }
  return null;
}

/* ── KSD8 — per-layer content hash + import dedup ─────────────────── */

/**
 * Compute a deterministic content hash for a layer-like object.
 *
 * Inputs: name + category + geometry.type + every coordinate (rounded
 * to 6 decimals — `1.0e-6 deg` ≈ 0.1 m, well below KMZ vertex noise so
 * floating-point round-trip noise can't fingerprint a re-upload as new).
 *
 * Output: a 16-char hex digest. Uses a simple non-cryptographic hash
 * (djb2 over the canonical-string form) — sufficient for collision
 * avoidance at the per-site scale (1k layers max). Crypto-grade
 * `sha256(file)` already lives at the `SiteDocument.contentHash`
 * boundary; per-layer is a lighter touch.
 */
export function computeLayerContentHash(layer: {
  name: string;
  category: string;
  geometry: { type: string; coordinates: Array<{ lat: number; lng: number }> };
}): string {
  const ROUND = 1e6;
  const coords = layer.geometry.coordinates
    .map(p => `${Math.round(p.lat * ROUND) / ROUND},${Math.round(p.lng * ROUND) / ROUND}`)
    .join('|');
  const canonical = `${layer.category}::${layer.name}::${layer.geometry.type}::${coords}`;
  // djb2 — 32-bit rolling hash, returned as zero-padded 16-char hex.
  let h = 5381;
  for (let i = 0; i < canonical.length; i++) {
    h = ((h << 5) + h + canonical.charCodeAt(i)) | 0;
  }
  // Two djb2 passes (forward + reverse) → 64-bit width worth of state.
  let g = 0;
  for (let i = canonical.length - 1; i >= 0; i--) {
    g = ((g << 5) + g + canonical.charCodeAt(i)) | 0;
  }
  const hex = (h >>> 0).toString(16).padStart(8, '0') + (g >>> 0).toString(16).padStart(8, '0');
  return hex;
}

export interface MergeImportedLayersResult<T extends { contentHash?: string }> {
  /** The merged layer set — existing layers ++ unique-new layers. */
  merged: T[];
  /** New layers actually added (their contentHash didn't already exist). */
  added: T[];
  /** New layers skipped because their contentHash matched an existing layer. */
  skipped: T[];
}

/**
 * KSD8 — Merge a freshly-imported KMZ's layers into a site's existing
 * `siteLayers`, skipping any whose `contentHash` already exists.
 *
 * Existing layers without a `contentHash` field are hashed lazily so
 * pre-KSD8 sites participate in dedup without a backfill pass.
 *
 * Imports are EVERY layer in the new KMZ; the helper stamps a
 * `contentHash` on each before deciding inclusion. The returned
 * `merged` array carries the existing layers first (preserving order)
 * followed by the new uniques (in the order the parser surfaced
 * them) — newest-last so user-driven re-orderings are stable.
 */
/**
 * R-131-2 — Dedup imported layers by id OR contentHash.
 *
 * Pre-R-131-2 this matched on `contentHash` only, which broke as soon as
 * the user edited an imported layer locally: the existing row's hash
 * shifted away from the incoming feature's canonical hash, so the next
 * regional re-fetch read it as "new" and appended a duplicate copy of the
 * feature the user had just renamed. With deterministic ids (R-131-2 in
 * esriJsonToSiteLayers + parsedToSiteLayer) the *id* is stable across
 * re-imports of the same feature; matching by either signal makes the
 * merge tolerant of local renames AND still catches imports from sources
 * that don't expose stable feature ids.
 *
 * Returned layers preserve the existing row's user edits (name / color /
 * description / visible) on a match — the incoming refetch never clobbers
 * user intent.
 */
export function mergeImportedLayers<
  T extends {
    id?: string;
    name: string;
    category: string;
    geometry: { type: string; coordinates: Array<{ lat: number; lng: number }> };
    contentHash?: string;
  },
>(
  existing: T[],
  incoming: T[],
): MergeImportedLayersResult<T> {
  const seenIds = new Set<string>();
  const seenHashes = new Set<string>();
  const merged: T[] = [];
  for (const e of existing) {
    const h = e.contentHash ?? computeLayerContentHash(e);
    if (e.id) seenIds.add(e.id);
    seenHashes.add(h);
    merged.push({ ...e, contentHash: h });
  }
  const added: T[] = [];
  const skipped: T[] = [];
  for (const n of incoming) {
    const h = n.contentHash ?? computeLayerContentHash(n);
    const idMatch = n.id ? seenIds.has(n.id) : false;
    if (idMatch || seenHashes.has(h)) {
      skipped.push({ ...n, contentHash: h });
      continue;
    }
    if (n.id) seenIds.add(n.id);
    seenHashes.add(h);
    const stamped = { ...n, contentHash: h };
    merged.push(stamped);
    added.push(stamped);
  }
  return { merged, added, skipped };
}
